import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeftIcon } from "lucide-react";
import * as React from "react";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Category } from "~/api/model";
import {
  getListOrganizationCategoriesQueryKey,
  useCreateOrganizationCategory,
  useDeleteOrganizationCategory,
  useListOrganizationCategories,
} from "~/api/organization/organization";
import { AutoResizeTextarea } from "~/components/ui/auto-resize-textarea";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Modal } from "~/components/ui/modal";
import {
  getOccupiedColors,
  getVacantCategoryColor,
  useOrganizationParams,
} from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { AutoFillButton } from "./AutoFillButton";

type CreateCategoryDataWithAutoFill = {
  name: string;
  color: string;
  definition?: string;
  namingConvention?: string;
  keyTopics?: string;
  expectedContent?: string;
  recognitionTips?: string;
  length?: string;
  isFallback: boolean;
  autoFillExamples?: boolean;
  autoFillField?:
    | "definition"
    | "expectedContent"
    | "keyTopics"
    | "recognitionTips"
    | "namingExample";
};

export enum CreateCategoryStep {
  NAME_AND_DEFINITION = 1,
  NAMING_CONVENTIONS = 2,
  CONTENT_DETAILS = 3,
}

interface CreateCategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CreateCategoryModal({
  open,
  onOpenChange,
}: CreateCategoryModalProps) {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: createCategory } = useCreateOrganizationCategory();
  const { mutateAsync: deleteCategory } = useDeleteOrganizationCategory();
  const { toast } = useToast();
  const intl = useIntl();
  const [step, setStep] = useState(CreateCategoryStep.NAME_AND_DEFINITION);
  const [formData, setFormData] = useState({
    name: "",
    definition: "",
    namingConvention: "",
    namingExample: "",
    expectedContent: "",
    keyTopics: "",
    length: "",
    color: "",
    recognitionTips: "",
  });
  const { data: categories = [] } =
    useListOrganizationCategories(organizationId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === CreateCategoryStep.NAME_AND_DEFINITION) {
      setStep(CreateCategoryStep.NAMING_CONVENTIONS);
    } else if (step === CreateCategoryStep.NAMING_CONVENTIONS) {
      setStep(CreateCategoryStep.CONTENT_DETAILS);
    } else {
      // Handle form submission
      const occupiedColors = getOccupiedColors(categories);
      const color = formData.color || getVacantCategoryColor(occupiedColors);

      // Close modal immediately
      onOpenChange(false);
      setStep(CreateCategoryStep.NAME_AND_DEFINITION);

      // Create in background and update cache with result
      createCategory({
        organizationId: organizationId,
        data: {
          name: formData.name,
          definition: formData.definition,
          color: color,
          isFallback: false,
          namingConvention: formData.namingConvention,
          namingExample: formData.namingExample,
          expectedContent: formData.expectedContent,
          keyTopics: formData.keyTopics,
          length: formData.length,
          recognitionTips: formData.recognitionTips,
        },
      })
        .then((newCategory) => {
          queryClient.setQueryData(
            getListOrganizationCategoriesQueryKey(organizationId),
            (oldData: Category[] | undefined) => {
              if (!oldData) return [newCategory];
              return [...oldData, newCategory];
            }
          );
        })
        .catch(() => {
          toast({
            variant: "destructive",
            title: intl.formatMessage({ id: "error" }),
            description: intl.formatMessage({
              id: "createCategory.createError",
            }),
          });
        });
    }
  };

  const handleBack = () => {
    if (step === CreateCategoryStep.CONTENT_DETAILS) {
      setStep(CreateCategoryStep.NAMING_CONVENTIONS);
    } else if (step === CreateCategoryStep.NAMING_CONVENTIONS) {
      setStep(CreateCategoryStep.NAME_AND_DEFINITION);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setStep(CreateCategoryStep.NAME_AND_DEFINITION);
  };

  const [isAutoFilling, setIsAutoFilling] = useState<string | null>(null);

  const handleAutoFill = async (
    fieldType:
      | "definition"
      | "expectedContent"
      | "keyTopics"
      | "recognitionTips"
      | "namingExample"
  ) => {
    if (isAutoFilling) return;

    if (fieldType === "namingExample" && !formData.namingConvention.trim())
      return;
    if (fieldType !== "definition" && !formData.name.trim()) return;

    setIsAutoFilling(fieldType);
    try {
      const occupiedColors = getOccupiedColors(categories);
      const tempColor = getVacantCategoryColor(occupiedColors);

      const requestData = {
        name:
          formData.name ||
          intl.formatMessage({ id: "createCategory.temporaryCategory" }),
        color: tempColor,
        definition: formData.definition,
        namingConvention: formData.namingConvention,
        keyTopics: formData.keyTopics,
        expectedContent: formData.expectedContent,
        recognitionTips: formData.recognitionTips,
        isFallback: false,
        autoFillExamples: true,
        autoFillField: fieldType,
      } as CreateCategoryDataWithAutoFill;

      const result = await createCategory({
        organizationId: organizationId,
        data: requestData,
      });

      if (fieldType === "namingExample") {
        setFormData((prev) => ({
          ...prev,
          namingExample: result.namingExample || "",
        }));
      } else if (fieldType === "definition") {
        setFormData((prev) => ({
          ...prev,
          definition: result.definition || "",
        }));
      } else if (fieldType === "expectedContent") {
        setFormData((prev) => ({
          ...prev,
          expectedContent: result.expectedContent || "",
        }));
      } else if (fieldType === "keyTopics") {
        setFormData((prev) => ({
          ...prev,
          keyTopics: result.keyTopics || "",
        }));
      } else if (fieldType === "recognitionTips") {
        setFormData((prev) => ({
          ...prev,
          recognitionTips: result.recognitionTips || "",
        }));
      }

      if (result.id) {
        try {
          await deleteCategory({
            organizationId: organizationId,
            categoryId: result.id.toString(),
          });
        } catch (deleteError) {}
      }
    } catch (error) {
      toast({
        title: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({ id: "createCategory.autoFillError" }),
        variant: "destructive",
      });
    } finally {
      setIsAutoFilling(null);
    }
  };

  useEffect(() => {
    if (open) {
      setFormData({
        name: "",
        definition: "",
        expectedContent: "",
        keyTopics: "",
        length: "",
        namingConvention: "",
        namingExample: "",
        color: "",
        recognitionTips: "",
      });
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={
        <div className="flex flex-col gap-[6px]">
          <span className="text-xs text-muted-foreground">
            {intl.formatMessage({ id: "createCategory.step" }, { step })}
          </span>
          <span className="text-[18px] font-semibold">
            {intl.formatMessage({ id: "createCategory.title" })}
          </span>
        </div>
      }
      description={
        step === CreateCategoryStep.NAME_AND_DEFINITION
          ? intl.formatMessage({ id: "createCategory.step1Description" })
          : step === CreateCategoryStep.NAMING_CONVENTIONS
            ? intl.formatMessage({ id: "createCategory.step2Description" })
            : intl.formatMessage({ id: "createCategory.step3Description" })
      }
      content={
        <form onSubmit={handleSubmit} id="create-category-form">
          <div>
            {step === CreateCategoryStep.NAME_AND_DEFINITION ? (
              <div className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="name">
                    {intl.formatMessage({ id: "categorySettings.name" })}
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder={intl.formatMessage({
                      id: "categorySettings.namePlaceholder",
                    })}
                    className="shadow-none"
                  />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="definition">
                      {intl.formatMessage({
                        id: "categorySettings.definition",
                      })}
                    </Label>
                    <AutoFillButton
                      onClick={() => handleAutoFill("definition")}
                      disabled={!formData.name.trim()}
                      isLoading={isAutoFilling === "definition"}
                    />
                  </div>
                  <AutoResizeTextarea
                    id="definition"
                    value={formData.definition}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setFormData({ ...formData, definition: e.target.value })
                    }
                    placeholder={intl.formatMessage({
                      id: "categorySettings.definitionPlaceholder",
                    })}
                    className="shadow-none"
                  />
                </div>
              </div>
            ) : step === CreateCategoryStep.NAMING_CONVENTIONS ? (
              <div className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="naming-conventions">
                    {intl.formatMessage({
                      id: "categorySettings.namingConvention",
                    })}
                  </Label>
                  <Input
                    id="naming-conventions"
                    value={formData.namingConvention}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        namingConvention: e.target.value,
                      })
                    }
                    placeholder={intl.formatMessage({
                      id: "categorySettings.namingConventionPlaceholder",
                    })}
                    className="shadow-none"
                  />
                  {!formData.namingConvention && (
                    <p className="text-sm text-muted-foreground">
                      {intl.formatMessage({
                        id: "categorySettings.namingConventionHint",
                      })}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="examples">
                      {intl.formatMessage({ id: "categorySettings.examples" })}
                    </Label>
                    <AutoFillButton
                      onClick={() => handleAutoFill("namingExample")}
                      disabled={!formData.namingConvention.trim()}
                      isLoading={isAutoFilling === "namingExample"}
                    />
                  </div>
                  <AutoResizeTextarea
                    id="naming-example"
                    value={formData.namingExample}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setFormData({
                        ...formData,
                        namingExample: e.target.value,
                      })
                    }
                    placeholder={intl.formatMessage({
                      id: "categorySettings.examplesPlaceholder",
                    })}
                    className="shadow-none"
                  />
                  <p className="text-sm text-muted-foreground">
                    {intl.formatMessage({
                      id: "categorySettings.examplesHint",
                    })}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="expected-content">
                      {intl.formatMessage({
                        id: "categorySettings.expectedContent",
                      })}
                    </Label>
                    <AutoFillButton
                      onClick={() => handleAutoFill("expectedContent")}
                      disabled={!formData.name.trim()}
                      isLoading={isAutoFilling === "expectedContent"}
                    />
                  </div>
                  <AutoResizeTextarea
                    id="expected-content"
                    value={formData.expectedContent}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setFormData({
                        ...formData,
                        expectedContent: e.target.value,
                      })
                    }
                    placeholder={intl.formatMessage({
                      id: "categorySettings.expectedContentPlaceholder",
                    })}
                    className="shadow-none"
                  />
                </div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="key-topics">
                      {intl.formatMessage({ id: "categorySettings.keyTopics" })}
                    </Label>
                    <AutoFillButton
                      onClick={() => handleAutoFill("keyTopics")}
                      disabled={!formData.name.trim()}
                      isLoading={isAutoFilling === "keyTopics"}
                    />
                  </div>
                  <AutoResizeTextarea
                    id="key-topics"
                    value={formData.keyTopics}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setFormData({ ...formData, keyTopics: e.target.value })
                    }
                    placeholder={intl.formatMessage({
                      id: "categorySettings.keyTopicsPlaceholder",
                    })}
                    className="shadow-none"
                  />
                </div>

                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="recognition-tips">
                      {intl.formatMessage({
                        id: "categorySettings.recognitionTips",
                      })}
                    </Label>
                    <AutoFillButton
                      onClick={() => handleAutoFill("recognitionTips")}
                      disabled={!formData.name.trim()}
                      isLoading={isAutoFilling === "recognitionTips"}
                    />
                  </div>
                  <AutoResizeTextarea
                    id="recognition-tips"
                    value={formData.recognitionTips}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setFormData({
                        ...formData,
                        recognitionTips: e.target.value,
                      })
                    }
                    placeholder={intl.formatMessage({
                      id: "categorySettings.recognitionTipsPlaceholder",
                    })}
                    className="shadow-none"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="length">
                    {intl.formatMessage({ id: "categorySettings.length" })}
                  </Label>
                  <Input
                    id="length"
                    value={formData.length}
                    onChange={(e) =>
                      setFormData({ ...formData, length: e.target.value })
                    }
                    placeholder={intl.formatMessage({
                      id: "categorySettings.lengthPlaceholder",
                    })}
                    className="shadow-none"
                  />
                </div>
              </div>
            )}
          </div>
        </form>
      }
      footer={
        <>
          {step !== CreateCategoryStep.NAME_AND_DEFINITION && (
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              className="sm:mr-auto"
            >
              <ChevronLeftIcon className="w-4 h-4 mr-2" />
              {intl.formatMessage({ id: "createCategory.back" })}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={handleCancel}>
            {intl.formatMessage({ id: "cancel" })}
          </Button>
          <Button type="submit" form="create-category-form">
            {step === CreateCategoryStep.CONTENT_DETAILS
              ? intl.formatMessage({ id: "createCategory.create" })
              : intl.formatMessage({ id: "createCategory.continue" })}
          </Button>
        </>
      }
    />
  );
}
